import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import List from "@material-ui/core/List";
import { makeStyles, createStyles } from "@material-ui/core/styles";

import type { IYoelloTheme } from '../../yoello-lib/modules/themes/IYoelloTheme';

import Logo from "../Logo";
import { IReduxAppState } from "../../reducers";
import IVenueSettings from "../../interfaces/IVenueSettings";
import {
  MenuContainerTabsNested,
  ISidebarItems,
  MenuContainerTabs,
} from "../Routes";
import SideBarListItem from "./SideBarListItem";
import SideBarListParent from "./SideBarListParent";
import { VenueCard } from "../ui/VenueCard";
import { setDisabledRoutes } from "../../actions/disabledRoutes";
import { disableReports } from "../../utils/disableRoutes";
import { OnboardingSource } from "../menu/payments/types.d";
import { IntegrationsList } from "../orders/constants";
import { OnboardingSources } from "../signup/types";
import { useFlags } from "launchdarkly-react-client-sdk";


const background = "#F3F4F9";
const textPrimary = "#2C2C2C";
const textSecondary = "#737FA4";
const gutterWidth = "1vw";
const gutterHeight = "0.49vh";
const borderRadius = "6px";

const useStyles = makeStyles((theme: IYoelloTheme) =>
  createStyles({
    "& a": {
      color: theme.palette.text.secondary,
    },
    wrapper: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },
    toolbar: {
      ...theme.mixins.toolbar,
      minHeight: `${theme.custom?.sidebar?.logoHeight || '60px'} !important`,
      position: "relative",
      display: "flex",
      alignContent: "center",
      alignItems: "center",
      justifyContent: "center",
      paddingTop: "0.75rem",
    },
    logo: {
      height: theme.custom?.sidebar?.logoHeight || '60px',
      position: "relative",
      "& .logo": {
        position: "absolute",
        left: "0",
        transform: "translate(0px, 8px) scale(0.8)",
        marginBottom: "12px",
      },
    },
    badge: {},
    navigation: {
      "& a": {
        textDecoration: "none",
      },
      flexGrow: 10,
      overflow: "auto",
      "& .MuiListItem-root": {
        paddingTop: `calc(${gutterHeight} / 2)`,
        paddingBottom: `calc(${gutterHeight} / 2)`,
        "&:hover": {
          "& .navigation__menu-item:not(.selected)": {
            backgroundColor: "#FCFBFD",
            "&.selected": {
              cursor: "default",
            },
          },
        },
        "& .navigation__menu-item.selected .sidebar-icon": {
          opacity: 1,
        },
      },
      "& .navigation__menu-parent": {
        "& .navigation__menu-parent__expand": {
          color: "#AAB6D3",
          transition: "0.25s ease-in-out",
          transform: "rotate(90deg)",
          "&.open": {
            transform: "rotate(180deg)",
          },
        },
      },
      "& .navigation__menu-parent, & .navigation__menu-item": {
        cursor: "pointer",
        boxSizing: "border-box",
        width: "100%",
        borderRadius: borderRadius,
        margin: `${gutterHeight} 0`,
        paddingLeft: gutterWidth,
        paddingRight: gutterWidth,
        transition: "0.25s ease-in-out",
        "& .navigation__menu-parent__inner": {
          paddingTop: gutterHeight,
          paddingBottom: gutterHeight,
          borderRadius: borderRadius,
          transition: "0.2s ease-in-out",
          "&.selected": {
            backgroundColor: "#FAFBFD",
            "& .MuiListItemIcon-root": {
              "& svg > path, & svg > rect": {
                stroke: textSecondary,
              },
            },
            "& > .MuiListItem-root > .MuiListItemText-root > .MuiTypography-root": {
              fontWeight: "600",
              color: textPrimary,
            },
          },
        },
        "& .MuiListItemIcon-root": {
          marginLeft: "12px",
          minWidth: "35px",
          "& svg > path, & svg > rect": {
            transition: "0.25s ease-in-out",
          },
        },
        "& .MuiListItemText-root": {
          color: textSecondary,
        },
      },
      "& .navigation__menu-item": {
        display: "flex",
        alignItems: "center",
        paddingTop: gutterHeight,
        paddingBottom: gutterHeight,
        "&.selected": {
          "background-color": background,
          "& .MuiListItemIcon-root": {
            "& svg > path, & svg > rect": {
              stroke: textSecondary,
            },
          },
          "& .MuiListItemText-root": {
            "& .MuiTypography-root": {
              color: textPrimary,
              fontWeight: "600",
            },
          },
        },
      },
    },
  })
);

interface ISidebar {
  venueSettingsData?: IVenueSettings;
  setMobileOpen: (active: boolean) => void;
  isLoading?: boolean;
}

export const parseSidebarItems = (
  items: ISidebarItems,
  permissions: any,
  venueSettingsData: IVenueSettings | undefined,
  isEposNow: boolean,
  isSquare: boolean,
  isPay360: boolean,
  setMobileOpen: (active: boolean) => void,
  nested: boolean,
  disabledRoutes: string[],
  selected: string,
  venueHasAccessToOB:boolean,
  onboardingSource?:string,
  isLoading?: boolean,
  isRMS?: boolean,
  venueCountry?: string,
  isStripe?: boolean,
) => {
  const isIntegration = IntegrationsList.includes(onboardingSource?.toUpperCase() as OnboardingSources);
  const hideOpenBanking = isIntegration;
  const isNcr = onboardingSource === OnboardingSource.NCR;
  const isOracle = onboardingSource === OnboardingSource.ORACLE;
  const isZonal = onboardingSource === OnboardingSource.ZONAL;
  const isTevalis = onboardingSource === OnboardingSource.TEVALIS;
  const isDeliverect = onboardingSource === OnboardingSource.DELIVERECT;
    
  return Object.values(items)
    .filter(
      (tab) =>
        tab.key !== MenuContainerTabs.PAYMENTS.key ||
        venueSettingsData?.visibility_merchant_dashboard_payments
    )
    .map((item, index) => {
      if (permissions && item.requiredPermission) {
        if (!permissions[item.requiredPermission]) {
          return null;
        }
      }
      if (!isEposNow && item.key === MenuContainerTabs.EPOSNOW.key) {
        return null;
      }
      if (!isSquare && item.key === MenuContainerTabs.SQUARE_SYNC.key) {
        return null;
      }
      if ((((venueCountry && venueCountry !== "GB") || isSquare || isPay360 || isEposNow || !venueHasAccessToOB || hideOpenBanking))
        && item.key === MenuContainerTabs.PAYMENTSADULT.key) {
        return null;
      }
      if (((venueCountry && venueCountry === "GB" && !isPay360 && !isEposNow && !hideOpenBanking) && venueHasAccessToOB)
        && item.key === MenuContainerTabs.PAYMENTS.key) {
        return null;
      }
      if (
        isSquare &&
        (item.key === MenuContainerTabs.REPORTS.key ||
          item.key === MenuContainerTabs.TAXES.key ||
          item.key === MenuContainerTabs.PREFERENCES.key ||
          item.key === MenuContainerTabs.EXTRAS.key ||
          item.key === MenuContainerTabs.DISCOUNTS.key)
      ) {
        return null;
      }
      if (
        (isSquare || isEposNow || isRMS || isStripe) &&
        item.key === MenuContainerTabs.SUBSCRIPTIONS.key
      ) {
        return null;
      }
      if (
        (isSquare || isEposNow) &&
        (item.key === MenuContainerTabs.NEW_ORDERS.key ||
          item.key === MenuContainerTabs.OPEN_ORDERS.key ||
          item.key === MenuContainerTabs.PRINTER_SETTINGS.key
        )
      ) {
        return null;
      }
      if (isIntegration &&
        (
          item.key === MenuContainerTabs.PRODUCTS.key || 
          item.key === MenuContainerTabs.TAXES.key|| 
          item.key === MenuContainerTabs.NEW_ORDERS.key || 
          item.key === MenuContainerTabs.OPEN_ORDERS.key  ||
          item.key === MenuContainerTabs.PRINTER_SETTINGS.key
        )
        ) {
        return null
      }

      // Hide discounts for all integrations other than Deliverect
      if ((isIntegration && !isDeliverect) && (item.key === MenuContainerTabs.DISCOUNTS.key)){
        return null
      }

      // If an integration has Menu Sync add it inside the if check so the menu sync tab shows up
      if (
        (!isNcr && !isOracle && !isZonal && !isTevalis) &&
        (item.key === MenuContainerTabs.MENU_SYNC.key)
      ) {
        return null;
      }

      return "items" in item ? (
        <SideBarListParent
          item={item}
          key={index}
          index={index}
          isSquare={isSquare}
          isPay360={isPay360}
          setMobileOpen={setMobileOpen}
          venueSettingsData={venueSettingsData}
          isEposNow={isEposNow}
          disabledRoutes={disabledRoutes}
          selected={selected}
          isLoading={isLoading}
          venueCountry={venueCountry}
          permissions={permissions}
        />
      ) : (
        <SideBarListItem
          item={item}
          key={index}
          index={index}
          setMobileOpen={setMobileOpen}
          nested={nested}
          disabledRoutes={disabledRoutes}
          selected={selected}
          isLoading={isLoading}
        />
      );
    });
};

const Sidebar = (props: ISidebar) => {
  const { setMobileOpen, isLoading = false } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const {hasObAccess} = useFlags();
  
  const selected = useSelector(
    (state: IReduxAppState) => state.sidebar.position.key
  );
  const disabledRoutes = useSelector(
    (state: IReduxAppState) => state.disabledRoutes
  );
  const selectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  );
  const venueData = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId]?.venueData
  );
  const venueLocation = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId]?.venueLocation
  );
  const venueSettings = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId]?.venueSettings
  );
  const permissions = useSelector(
    (state: IReduxAppState) => state.permissions
  );


  const isEposNow = useSelector((state: IReduxAppState) => state.isEposNow);
  const isPay360 = venueData?.payment_processor === "PAY360";
  const [isSquare, setIsSquare] = useState(false);
  const [venueHasAccessToOB, setVenueHasAccessToOB] = useState(false);

  useEffect(() => {
    setVenueHasAccessToOB(hasObAccess && !isEposNow && !isPay360);
    //eslint-disable-next-line
  }, [selectedVenueId]);

  useEffect(() => {
    setIsSquare(venueData?.payment_processor === "SQUARE");
    if (venueData && venueLocation && venueSettings) {
      const isApp = sessionStorage.getItem("isApp");
      if (isApp) {
        const disableList = disableReports()[0];
        dispatch(setDisabledRoutes([disableList]));
      }
    }
    //eslint-disable-next-line
  }, []);

  return (
    <div
      data-component-name={isEposNow ? "EposNow" : "NotEposNow"}
      className={classes.wrapper}
    >
      <div className={classes.toolbar}>
        <div className={classes.logo}>
          <Logo height="100%" />
        </div>
      </div>

      <VenueCard isLoading={isLoading} />

      <List className={classes.navigation}>
        {parseSidebarItems(
          MenuContainerTabsNested,
          permissions,
          venueSettings,
          isEposNow,
          isSquare,
          isPay360,
          setMobileOpen,
          false,
          disabledRoutes,
          selected,
          venueHasAccessToOB,
          venueData?.onboarding_source,
          isLoading,
          venueData?.onboarding_source === OnboardingSource.RMS,
          venueLocation?.address_country,
          venueData?.stripe_connect_account_id !== null,
        )}
      </List>
    </div>
  );
};

export default Sidebar;
